import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { createContent } from '@atlassian/embedded-confluence';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { RouterActionsType } from '@atlassian/react-resource-router';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import { PARENT_PRODUCT } from '../../../../common/constants.tsx';
import { fireErrorAnalyticsEvents } from '../../../../common/utils.tsx';
import type { State } from '../../types.tsx';
import { navigateCreateDraftRoute } from '../navigate-create-draft-route/index.tsx';
import { setRedirectionData } from '../redirection-data/index.tsx';

const EVENT_NAME = 'createNewConfluencePage';

type BodyFormat = 'wiki' | 'atlas_doc_format' | 'plain' | 'raw';

type ContentBody = {
	[key in BodyFormat]?: {
		value: string;
		representation: BodyFormat;
	};
};

export const fetchDraftAndNavigateToEditor =
	({
		spaceKey,
		routerActions,
		projectKey,
		projectId,
		onCloseRedirectUrl = '',
		onPublishRedirectUrl = '',
		analyticsEvent,
		topicId,
		topicName,
		bodyText,
		isJcs,
		helpCenterId,
	}: {
		spaceKey: string;
		routerActions: RouterActionsType;
		projectKey: string;
		projectId: number;
		onCloseRedirectUrl?: string;
		onPublishRedirectUrl?: string;
		analyticsEvent: UIAnalyticsEvent;
		topicId?: string;
		topicName?: string;
		bodyText?: string;
		isJcs?: boolean;
		helpCenterId?: string;
	}) =>
	async ({ setState, getState, dispatch }: StoreActionApi<State>) => {
		try {
			setState({ ...getState(), isFetchingDraftDetails: true });

			const bodyADFText = (content: string): ContentBody => {
				return {
					atlas_doc_format: {
						value: JSON.stringify(JSON.parse(content)),
						representation: 'atlas_doc_format',
					},
				};
			};

			const createContentParams = {
				spaceKey,
				parentProductContentContainerId: String(projectId),
				parentProduct: PARENT_PRODUCT,
				title: topicName,
				body: bodyText ? bodyADFText(bodyText) : undefined,
			};
			const { contentId, draftShareId } = await createContent(createContentParams);
			setState({
				...getState(),
				articleId: contentId,
				draftShareId,
				isFetchingDraftDetails: false,
			});
			fireTrackAnalytics(analyticsEvent, `${EVENT_NAME} succedded`, {
				topicId,
				articleId: contentId,
			});
			dispatch(setRedirectionData({ onCloseRedirectUrl, onPublishRedirectUrl }));
			dispatch(
				navigateCreateDraftRoute({
					routerActions,
					projectKey,
					topicId,
					selectedSpaceKey: spaceKey,
					isJcs,
					helpCenterId,
				}),
			);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			setState({
				...getState(),
				isFetchingDraftDetails: false,
				showConfluenceCreatePageError: true,
			});
			fireErrorAnalyticsEvents(error, EVENT_NAME, 'knowledgeCommon');
		}
	};
